import type { EnlacesDelNegocio, GuionConfig, InstallationConfig, MessagesConfig, PropositoDeVideo } from '@/api/installation.api';
import { fromConfig, toPayload } from '@/pages/installation/InstallationConfigPage';

/**
 * AJUSTES DEL AGENTE (tanda 3, F19): lo que el dueño tiene que poder cambiar sin
 * tocar la base. Los mínimos por zona, las franjas del Zoom, el trato, sus enlaces,
 * sus videos de presentación (tanda 5) y su plantilla. Antes vivían en la página de Instalación, que no está en su menú
 * y ni siquiera mostraba los mínimos ni las franjas.
 *
 * Guarda con el MISMO PUT completo que Instalación (fromConfig/toPayload) y cambia
 * solo lo suyo: así no se borra nada que esta página no muestra.
 */

/** Las redes que el dueño puede dar, en el orden de la pantalla. */
export const REDES: ReadonlyArray<{ clave: keyof EnlacesDelNegocio; etiqueta: string }> = [
  { clave: 'web', etiqueta: 'Web' },
  { clave: 'instagram', etiqueta: 'Instagram' },
  { clave: 'facebook', etiqueta: 'Facebook' },
  { clave: 'tiktok', etiqueta: 'TikTok' },
];

/** Los videos de presentación, en el orden de la pantalla. */
export const VIDEOS_DE_PRESENTACION: ReadonlyArray<{ clave: PropositoDeVideo; etiqueta: string; detalle?: string }> = [
  { clave: 'pareja', etiqueta: 'Vienen en pareja', detalle: 'escriben los dos' },
  { clave: 'uno_solo', etiqueta: 'Escribe uno solo', detalle: 'todavía sin su pareja' },
  { clave: 'evento', etiqueta: 'El evento', detalle: 'cómo se vive por dentro' },
  { clave: 'generico', etiqueta: 'Genérico', detalle: 'si no encaja ninguno de los anteriores' },
];

export interface AjustesForm {
  /** Mínimo por zona, como texto del campo. Vacío = sin mínimo. Enrique no lo usa: su evento no tiene mínimo. */
  minimos: Record<string, string>;
  franjas: string[];
  trato: 'usted' | 'tu';
  enlaces: Record<keyof EnlacesDelNegocio, string>;
  /** Tanda 5: por propósito, la clave del video ('' = sin video) y lo que va con él. */
  videos: Record<PropositoDeVideo, { key: string; texto: string }>;
  /**
   * Tanda B: las entradas del evento, como texto de los campos.
   *
   * El precio va como texto y no como número a propósito: un campo numérico vacío es `NaN`,
   * y de ahí a guardar un precio roto hay un paso. Se valida al guardar.
   */
  entradas: { nivel: string; precio: string; incluye: string }[];
}

const ENLACE_COMPLETO = /^https?:\/\/\S+$/;

export function ajustesDesdeConfig(cfg: InstallationConfig): AjustesForm {
  const min = cfg.crm.minimoPorZona ?? {};
  const g = cfg.crm.guion ?? null;
  return {
    minimos: Object.fromEntries(cfg.crm.zonas.map((z) => [z, min[z] === undefined ? '' : String(min[z])])),
    franjas: [...(g?.franjasZoom ?? [])],
    trato: g?.trato === 'usted' ? 'usted' : 'tu',
    enlaces: Object.fromEntries(REDES.map((r) => [r.clave, g?.enlaces?.[r.clave] ?? ''])) as AjustesForm['enlaces'],
    videos: Object.fromEntries(
      VIDEOS_DE_PRESENTACION.map((v) => [v.clave, { key: g?.videos?.[v.clave]?.key ?? '', texto: g?.videos?.[v.clave]?.texto ?? '' }]),
    ) as AjustesForm['videos'],
    entradas: (g?.entradas ?? []).map((e) => ({ nivel: e.nivel, precio: String(e.precioDop), incluye: (e.incluye ?? []).join('; ') })),
  };
}

/** Cuántos niveles de entrada se admiten. El mismo tope que el servidor. */
export const MAX_ENTRADAS = 6;

/** Errores por campo (`minimo:<zona>`, `enlace:<red>`). Vacío = se puede guardar. */
export function erroresDeAjustes(f: AjustesForm): Record<string, string> {
  const e: Record<string, string> = {};
  for (const [zona, v] of Object.entries(f.minimos)) {
    if (v.trim() && !/^\d+$/.test(v.trim())) e[`minimo:${zona}`] = 'Solo el número en dólares, sin puntos ni comas (350000).';
  }
  for (const r of REDES) {
    const u = f.enlaces[r.clave].trim();
    if (u && !ENLACE_COMPLETO.test(u)) e[`enlace:${r.clave}`] = 'Pega el enlace completo, empezando por https://';
  }
  /**
   * Las entradas. Esto se valida con cuidado porque es lo único de esta pantalla que el
   * agente le DICE a un cliente: un precio mal escrito aquí es un precio mal dicho allá.
   * Una fila a medias no se guarda a medias; se dice qué falta.
   */
  f.entradas.forEach((x, i) => {
    const nivel = x.nivel.trim();
    const precio = x.precio.trim();
    if (!nivel && !precio) return; // una fila vacía se descarta, no es un error
    if (!nivel) e[`entrada:${i}:nivel`] = 'Ponle el nombre (Gold, Platinum…).';
    if (!precio) e[`entrada:${i}:precio`] = 'Ponle el precio en pesos.';
    else if (!/^\d+$/.test(precio)) e[`entrada:${i}:precio`] = 'Solo el número en pesos, sin puntos ni comas (6600).';
    else if (Number(precio) <= 0) e[`entrada:${i}:precio`] = 'El precio tiene que ser mayor que cero.';
  });
  const niveles = f.entradas.map((x) => x.nivel.trim().toLowerCase()).filter(Boolean);
  if (new Set(niveles).size !== niveles.length) e['entrada:repetida'] = 'Hay dos entradas con el mismo nombre.';
  return e;
}

/** La config completa para el PUT, con los ajustes puestos y todo lo demás tal cual. */
export function configConAjustes(cfg: InstallationConfig, f: AjustesForm, messages: MessagesConfig) {
  const base = toPayload(fromConfig(cfg));
  // Se parte de lo guardado: un mínimo de una zona que ya no está en la lista no se pierde.
  const minimoPorZona: Record<string, number> = { ...(cfg.crm.minimoPorZona ?? {}) };
  for (const [zona, v] of Object.entries(f.minimos)) {
    if (v.trim()) minimoPorZona[zona] = Number(v.trim());
    else delete minimoPorZona[zona];
  }
  const enlaces = Object.fromEntries(
    REDES.map((r) => [r.clave, f.enlaces[r.clave].trim()]).filter(([, u]) => u),
  ) as EnlacesDelNegocio;
  // Siempre va la lista entera: quitar el último video tiene que borrarlo, no conservar el guardado.
  // Un texto sin su video no se guarda: no hay con qué mandarlo.
  const videos: NonNullable<GuionConfig['videos']> = {};
  for (const { clave } of VIDEOS_DE_PRESENTACION) {
    const { key, texto } = f.videos[clave];
    if (key) videos[clave] = { key, ...(texto.trim() ? { texto: texto.trim() } : {}) };
  }
  // Siempre va la lista entera, igual que los videos: borrar la última entrada tiene que
  // borrarla de verdad. Y las filas vacías se van: una fila en blanco no es una entrada.
  const entradas = f.entradas
    .filter((x) => x.nivel.trim() && x.precio.trim())
    .map((x) => ({
      nivel: x.nivel.trim(),
      precioDop: Number(x.precio.trim()),
      ...(x.incluye.trim() ? { incluye: x.incluye.split(';').map((s) => s.trim()).filter(Boolean) } : {}),
    }));
  return {
    ...base,
    crm: {
      ...base.crm,
      minimoPorZona,
      ...(cfg.crm.guion
        ? { guion: { ...cfg.crm.guion, franjasZoom: f.franjas.map((x) => x.trim()).filter(Boolean), trato: f.trato, enlaces, videos, entradas } }
        : {}),
    },
    messages,
  };
}
